import { AlertTriangle, Download, FileArchive, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { formatFileSize } from "@/lib/files-format";

const dia = (d: Date | string | null | undefined) => (d ? new Date(d).toLocaleDateString("pt-BR") : "");
const botao =
  "inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-[12.5px] font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted";

/**
 * Exportar os dados da organização: um .zip com planilhas, ficha e arquivos de cada paciente, notas
 * fiscais e o resto dos arquivos. Gera em segundo plano; a tela acompanha e o dono recebe aviso.
 */
export function CartaoDeExportacao() {
  const utils = trpc.useUtils();
  const atual = trpc.organization.exportacao.atual.useQuery(undefined, {
    refetchInterval: (q) => (q.state.data?.status === "GERANDO" ? 4000 : false),
  });
  const pedir = trpc.organization.exportacao.pedir.useMutation({
    onSuccess: () => {
      toast.success("Exportação começou. Você recebe um aviso quando estiver pronta.");
      utils.organization.exportacao.atual.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  const baixar = trpc.organization.exportacao.baixar.useMutation({
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (e) => toast.error(e.message),
  });
  const e = atual.data;
  const gerando = e?.status === "GERANDO";

  return (
    <section className="rounded-lg border border-zinc-200 p-4 dark:border-border">
      <div className="flex items-start gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
          <FileArchive className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Exportar os dados</h3>
          <p className="text-[12.5px] text-zinc-600 dark:text-muted-foreground">
            Um .zip com as planilhas (pacientes, consultas, planos, pedidos, financeiro), a ficha e os arquivos de cada paciente, as notas fiscais e o
            resto dos arquivos. O prontuário precisa ser guardado por <b>no mínimo 20 anos</b> (Lei 13.787/2018) — guarde o arquivo.
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 pl-11">
        {atual.isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
        ) : gerando ? (
          <p className="inline-flex items-center gap-2 text-[12.5px] font-medium text-zinc-700 dark:text-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-brand-600" />
            Gerando a exportação… pode sair desta tela, você recebe um aviso quando ficar pronta.
          </p>
        ) : (
          <>
            {e?.disponivel && (
              <button type="button" disabled={baixar.isPending} onClick={() => baixar.mutate()} className={`${botao} border-emerald-300 text-emerald-800 hover:bg-emerald-50 dark:text-emerald-300`}>
                {baixar.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                Baixar .zip{e.tamanho ? ` (${formatFileSize(e.tamanho)})` : ""}
              </button>
            )}
            <button type="button" disabled={pedir.isPending} onClick={() => pedir.mutate()} className={botao}>
              {pedir.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : e ? <RefreshCw className="h-3.5 w-3.5" /> : <FileArchive className="h-3.5 w-3.5" />}
              {e ? "Gerar de novo" : "Gerar exportação"}
            </button>
            {e?.disponivel && <span className="text-[11.5px] text-zinc-500 dark:text-muted-foreground">Gerada em {dia(e.concluidaEm)} · disponível até {dia(e.expiraEm)}</span>}
            {e?.status === "PRONTA" && !e.disponivel && <span className="text-[11.5px] text-zinc-500 dark:text-muted-foreground">A última exportação venceu. Gere de novo.</span>}
          </>
        )}
      </div>
      {e?.status === "ERRO" && (
        <p className="mt-2 ml-11 flex items-start gap-1.5 text-[12px] text-red-700 dark:text-red-400">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />A última exportação falhou: {e.erro}
        </p>
      )}
    </section>
  );
}
